import { LEAD_STATUSES } from "../../Lead/constants/lead-statuses.ts"
import type {
  LeadContract,
  LeadProposal
} from "../../Lead/types/lead.documents.types.ts"
import type { Lead } from "../../Lead/types/lead.types.ts"
import { RESPONSIBLE_TYPES } from "../../Project/ProjectViewPage/constants/responsible-type-constants.ts"
import {
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_TYPES
} from "../../Project/ProjectViewPage/constants/finance-item-constants.ts"
import {
  INVOICE_STATUSES,
  INVOICE_TYPES
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import { MILESTONE_STATUSES } from "../../Project/ProjectViewPage/constants/milestone-statuses.ts"
import type { MilestoneDependency } from "../../Project/types/project-milestone-dependency.types.ts"
import { MilestoneDependencyType } from "../../Project/types/project-milestone-dependency.types.ts"
import type {
  Project,
  ProjectTypeDetail
} from "../../Project/types/project.types.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import type {
  ProjectMilestone,
  ProjectMilestoneFinanceItem
} from "../../Project/types/project-phases.types.tsx"

/**
 * ONE coherent, hardcoded "job" a brand-new account's Product Tour and its
 * Milestones/Finance/Invoices deep dives can walk through when the account
 * has zero real Leads/Projects — Sarah Miller's Kitchen Remodel, from first
 * contact through a finished job. Never written to the database, never
 * fetched from an API: this file is the only place this data exists.
 *
 * The milestone/finance/invoice records below are shaped exactly like the
 * real API responses (`ProjectMilestone`, `FinancialTransactionItem`,
 * `ProjectInvoice`) because they're rendered by the REAL production
 * components (`ProjectViewMilestonesList`, `ProjectViewFinanceList`,
 * `ProjectViewInvoicesList` — see `tour-preview/demo-sections/`), not a
 * simplified tour-only mockup. `TOUR_DEMO_FINANCE_ITEMS` is deliberately
 * derived by flattening every milestone's own `financial_items` — that
 * mirrors production, where a financial line lives inside its milestone and
 * has no endpoint of its own (see docs/kb/flows/project-milestones.md) — so
 * a Cabinets payable created under Milestones is *the same object* the
 * Finance tab lists under "Milestone: Cabinets," not a lookalike copy.
 */

export const TOUR_DEMO_PROJECT_ID = "demo-project"
export const TOUR_DEMO_LEAD_ID = "demo-lead"

const leadProjectTypeDetail = {
  id: "demo-lead-project-type-kitchen",
  name: "Kitchen Remodel",
  display_name: "Kitchen Remodel"
}

/** Cast, not fully populated: `Lead` carries ~90 fields; every component
 * this feeds (`LeadOverviewTab`, `LeadScopeTab`, `LeadDocumentsTab` and
 * their cards — all checked directly, presentational, props-only) only
 * reads the fields set here. */
export const TOUR_DEMO_LEAD_RECORD = {
  id: TOUR_DEMO_LEAD_ID,
  uid: "L-DEMO",
  lead_name: "Kitchen Remodel",
  customer_name: "Sarah Miller",
  customer: {
    id: "demo-customer",
    first_name: "Sarah",
    last_name: "Miller",
    email: "sarah.miller@example.com",
    phone: "(512) 555-0142"
  },
  // CONTRACT (not ESTIMATE): `leadDocumentVisibility` only shows the
  // Contracts section once the lead is off the proposal-only stage — with
  // both a demo Proposal and a demo Contract already on this record, the
  // Contract stage is the one where both sections show at once.
  status: LEAD_STATUSES.CONTRACT,
  status_name: "Contract",
  email: "sarah.miller@example.com",
  phone: "(512) 555-0142",
  address: "142 Willow Creek Dr",
  zip_code: "78701",
  state: "TX",
  city: "Austin",
  geolocation: null,
  preferred_contact_method: "email",
  source: "Website",
  property_ownership: "owner",
  project_type: [leadProjectTypeDetail.id],
  project_type_details: [leadProjectTypeDetail],
  timeline: "1-3 months",
  budget_range: "$40,000 - $60,000",
  needs_permit: true,
  needs_design: true,
  needs_license: false,
  supply_purchase_responsibility: "contractor",
  supply_purchase_responsibility_name: "Contractor supplies materials",
  external_purchases: [],
  comments:
    "Full kitchen renovation: cabinetry, countertops, appliances and flooring replacement.",
  scope:
    "Full kitchen renovation: cabinetry, countertops, appliances and flooring replacement.",
  sales_manager: [{ id: "demo-pm", first_name: "Demo", last_name: "PM" }],
  sales_manager_name: "Demo PM",
  total_budget: 48500,
  sales_price: 48500,
  planned_payable: 31200,
  created: "2026-08-20T00:00:00Z",
  appointment_date: "2026-09-22",
  appointment_time: "10:00:00",
  follow_up_date: "2026-09-22",
  follow_up_time: "10:00:00",
  operational_states: [{ state: "hot", label: "Hot" }],
  is_favorite: false,
  is_archived: false,
  is_closed_lost: false,
  is_project: false,
  attachments: [],
  notes: [],
  proposals: [],
  contracts: []
} as unknown as Lead

export const TOUR_DEMO_PROPOSAL_ID = "demo-proposal"
export const TOUR_DEMO_PROPOSALS: LeadProposal[] = [
  {
    id: TOUR_DEMO_PROPOSAL_ID,
    proposal_id: "PR-1001",
    project_type: leadProjectTypeDetail.id,
    team_id: "demo-team",
    status: "sent",
    status_display: "Sent to Client",
    version_number: 1,
    version_display: "v1",
    total_amount: "48500.00",
    created: "2026-08-25T00:00:00Z",
    updated: "2026-08-25T00:00:00Z",
    logo_url: null,
    customer_name: "Sarah Miller",
    lead_id: TOUR_DEMO_LEAD_ID,
    lead_name: "Kitchen Remodel",
    created_by_name: "Demo PM",
    attachments: [],
    attachments_count: 0,
    latest_attachment: null,
    submitted_attachment: null,
    submitted_for_review_at: null,
    reviewed_at: null,
    decline_reason: null,
    is_editable: false,
    is_latest_version: true,
    is_version_2: true,
    can_submit_for_review: false,
    can_create_contract: true,
    is_generated: true
  } as unknown as LeadProposal
]

export const TOUR_DEMO_CONTRACTS: LeadContract[] = [
  {
    id: "demo-contract",
    contract_id: "CT-1001",
    proposal_id: TOUR_DEMO_PROPOSAL_ID,
    project_type: leadProjectTypeDetail.id,
    team_id: "demo-team",
    status: "sent",
    status_display: "Sent to Client",
    proposal_display_id: "PR-1001",
    version_number: 1,
    version_display: "v1",
    total_amount: "48500.00",
    created: "2026-08-28T00:00:00Z",
    updated: "2026-08-28T00:00:00Z",
    logo_url: null,
    customer_name: "Sarah Miller",
    lead_id: TOUR_DEMO_LEAD_ID,
    lead_name: "Kitchen Remodel",
    created_by_name: "Demo PM",
    attachments: [],
    attachments_count: 0,
    latest_attachment: null,
    submitted_attachment: null,
    submitted_for_review_at: null,
    reviewed_at: null,
    decline_reason: null,
    is_editable: false,
    is_latest_version: true,
    can_submit_for_review: false,
    can_create_contract: false,
    can_send_to_client: false,
    is_generated: true
  } as unknown as LeadContract
]

TOUR_DEMO_LEAD_RECORD.proposals = TOUR_DEMO_PROPOSALS
TOUR_DEMO_LEAD_RECORD.contracts = TOUR_DEMO_CONTRACTS

export const TOUR_DEMO_LEAD_FILTER_OPTIONS = {
  status: [
    { label: "New", value: LEAD_STATUSES.NEW },
    { label: "Contacted", value: LEAD_STATUSES.CONTACTED },
    { label: "Estimate", value: LEAD_STATUSES.ESTIMATE },
    { label: "Won", value: LEAD_STATUSES.WON }
  ],
  sales_manager: [{ label: "Demo PM", value: "demo-pm" }],
  project_type: [{ label: "Kitchen Remodel", value: leadProjectTypeDetail.id }],
  source: [{ label: "Website", value: "website" }],
  state: [{ label: "TX", value: "TX" }],
  contact_method: [{ label: "Email", value: "email" }],
  operational_states: [{ label: "Hot", value: "hot" }],
  timeline: [{ label: "1-3 months", value: "1-3 months" }],
  budget_range: [{ label: "$40,000 - $60,000", value: "$40,000 - $60,000" }]
}

export const TOUR_DEMO_LEAD = {
  customerName: "Sarah Miller",
  jobName: "Kitchen Remodel",
  projectType: "Kitchen Remodel",
  source: "Website",
  stage: "Estimate",
  value: 48500,
  status: "Hot",
  followUp: "Tomorrow, 10:00 AM",
  scopeSummary:
    "Full kitchen renovation: cabinetry, countertops, appliances and flooring replacement.",
  proposalName: "Kitchen Remodel Proposal",
  contractName: "Kitchen Remodel Contract"
}

const projectTypeDetail: ProjectTypeDetail = {
  id: "demo-project-type-kitchen",
  occurrence: 1,
  name: "Kitchen Remodel",
  display_name: "Kitchen Remodel",
  category: 1,
  category_display: "Renovation",
  estimated_receivable: "48500.00",
  estimated_payable: "31200.00",
  contract_decision: 1,
  contract_decision_display: "Included"
}

/** Cast, not fully populated: `Project` carries ~60 fields, and every
 * component this file feeds (`ProjectViewMilestonesHeader`,
 * `ProjectViewFinanceList`, `ProjectViewInvoicesList`, …) was checked
 * directly and only reads the fields set here — `project_type_details`
 * chief among them, for the Header's per-type tab strip. */
export const TOUR_DEMO_PROJECT = {
  id: TOUR_DEMO_PROJECT_ID,
  uid: "P-DEMO",
  lead_name: "Miller Kitchen Remodel",
  customer: null,
  temporary_customer: {
    id: "demo-customer",
    first_name: "Sarah",
    last_name: "Miller"
  },
  address: "142 Willow Creek Dr",
  address_2: null,
  city: "Austin",
  state: "TX",
  zip_code: "78701",
  project_status: 2,
  project_status_name: "In Progress",
  project_managers: [{ id: "demo-pm", first_name: "Demo", last_name: "PM" }],
  sales_manager: [],
  start_date: "2026-09-01",
  end_date: "2026-11-05",
  sales_price: "48500.00",
  planned_payable: "31200.00",
  project_type_details: [projectTypeDetail]
} as unknown as Project

const financeItem = (
  overrides: Partial<ProjectMilestoneFinanceItem> &
    Pick<
      ProjectMilestoneFinanceItem,
      | "id"
      | "name"
      | "item_type"
      | "value"
      | "project_phase"
      | "project_phase_name"
    >
): ProjectMilestoneFinanceItem => ({
  item_type_display:
    overrides.item_type === FINANCE_ITEM_TYPES.PAYABLE
      ? "Payable"
      : "Receivable",
  comment: "",
  responsible_type: RESPONSIBLE_TYPES.CUSTOMER,
  responsible_type_display: "Customer",
  status: FINANCE_ITEM_STATUSES.PLANNED,
  status_display: "Planned",
  date: "2026-09-01",
  due_date: null,
  project: TOUR_DEMO_PROJECT_ID,
  project_name: TOUR_DEMO_PROJECT.lead_name,
  assignee: null,
  attachments: [],
  created: "2026-09-01T00:00:00Z",
  updated: "2026-09-01T00:00:00Z",
  ...overrides
})

// Design & Permit Fee — settled under Planning & Permits.
const financePlanningReceivable = financeItem({
  id: "demo-fi-planning-receivable",
  name: "Design & Permit Fee",
  item_type: FINANCE_ITEM_TYPES.RECEIVABLE,
  value: "2500.00",
  project_phase: "demo-milestone-planning",
  project_phase_name: "Planning & Permits",
  status: FINANCE_ITEM_STATUSES.COMPLETED,
  status_display: "Completed",
  date: "2026-09-01"
})

// Demo Crew payable — settled under Demolition.
const financeDemolitionPayable = financeItem({
  id: "demo-fi-demolition-payable",
  name: "Demo Crew",
  item_type: FINANCE_ITEM_TYPES.PAYABLE,
  value: "1800.00",
  project_phase: "demo-milestone-demolition",
  project_phase_name: "Demolition",
  responsible_type: RESPONSIBLE_TYPES.SUPPLIER,
  responsible_type_display: "Supplier",
  custom_subcontractor_name: "Metro Demo Crew",
  status: FINANCE_ITEM_STATUSES.COMPLETED,
  status_display: "Completed",
  date: "2026-09-10"
})

// The flagship connected example the spec asks for by name: a Cabinets
// receivable and two Cabinets payables, later mirrored on the Finance tab
// and on the Cabinet Progress Invoice.
export const CABINETS_RECEIVABLE_ID = "demo-fi-cabinets-receivable"
const financeCabinetsReceivable = financeItem({
  id: CABINETS_RECEIVABLE_ID,
  name: "Cabinet Progress Payment",
  item_type: FINANCE_ITEM_TYPES.RECEIVABLE,
  value: "12000.00",
  project_phase: "demo-milestone-cabinets",
  project_phase_name: "Cabinets",
  status: FINANCE_ITEM_STATUSES.PENDING,
  status_display: "Pending",
  due_date: "2026-10-10",
  date: "2026-09-26"
})
const financeCabinetsPayableSupplier = financeItem({
  id: "demo-fi-cabinets-payable-abc",
  name: "ABC Cabinets",
  item_type: FINANCE_ITEM_TYPES.PAYABLE,
  value: "7500.00",
  project_phase: "demo-milestone-cabinets",
  project_phase_name: "Cabinets",
  responsible_type: RESPONSIBLE_TYPES.SUPPLIER,
  responsible_type_display: "Supplier",
  custom_subcontractor_name: "ABC Cabinets",
  status: FINANCE_ITEM_STATUSES.PLANNED,
  status_display: "Planned",
  due_date: "2026-10-05",
  date: "2026-09-26"
})
const financeCabinetsPayableCrew = financeItem({
  id: "demo-fi-cabinets-payable-crew",
  name: "Installation Crew",
  item_type: FINANCE_ITEM_TYPES.PAYABLE,
  value: "1500.00",
  project_phase: "demo-milestone-cabinets",
  project_phase_name: "Cabinets",
  responsible_type: RESPONSIBLE_TYPES.CONTRACTOR,
  responsible_type_display: "Contractor",
  custom_subcontractor_name: "Premier Installation Crew",
  status: FINANCE_ITEM_STATUSES.PLANNED,
  status_display: "Planned",
  due_date: "2026-10-15",
  date: "2026-09-26"
})

const milestone = (
  overrides: Partial<ProjectMilestone> &
    Pick<ProjectMilestone, "id" | "name" | "order">
): ProjectMilestone => ({
  schedule_risk: "on_track",
  description: "",
  start_date: null,
  end_date: null,
  actual_end_date: null,
  phase: null,
  project_type: projectTypeDetail.id,
  project_type_assignment: projectTypeDetail.id,
  variance: null,
  is_enabled: true,
  is_started: false,
  is_finished: false,
  progress: 0,
  budget: null,
  duration_days: null,
  planned_payable: 0,
  planned_receivable: 0,
  status: MILESTONE_STATUSES.PLANNED,
  status_display: "Planned",
  attachments: [],
  created: "2026-09-01T00:00:00Z",
  updated: "2026-09-01T00:00:00Z",
  actual_received: 0,
  actual_paid: 0,
  planned_received: 0,
  committed_receivable: 0,
  committed_payable: 0,
  planned_paid: 0,
  financial_items: [],
  lead: TOUR_DEMO_LEAD_ID,
  milestone_category: 1,
  milestone_category_display: "Construction",
  dependency_count: 0,
  ...overrides
})

export const TOUR_DEMO_MILESTONES: ProjectMilestone[] = [
  milestone({
    id: "demo-milestone-planning",
    name: "Planning & Permits",
    order: 1,
    schedule_risk: "on_track",
    description: "Finalize design, pull the renovation permit.",
    start_date: "2026-09-01",
    end_date: "2026-09-08",
    actual_end_date: "2026-09-08",
    variance: 0,
    is_started: true,
    is_finished: true,
    progress: 100,
    status: MILESTONE_STATUSES.COMPLETED,
    status_display: "Completed",
    planned_receivable: 2500,
    actual_received: 2500,
    committed_receivable: 2500,
    financial_items: [financePlanningReceivable]
  }),
  milestone({
    id: "demo-milestone-demolition",
    name: "Demolition",
    order: 2,
    schedule_risk: "on_track",
    description: "Remove existing cabinetry, countertops and flooring.",
    start_date: "2026-09-09",
    end_date: "2026-09-12",
    actual_end_date: "2026-09-13",
    variance: -1,
    is_started: true,
    is_finished: true,
    progress: 100,
    status: MILESTONE_STATUSES.COMPLETED,
    status_display: "Completed",
    planned_payable: 1800,
    actual_paid: 1800,
    committed_payable: 1800,
    financial_items: [financeDemolitionPayable],
    dependency_count: 1
  }),
  milestone({
    id: "demo-milestone-rough-plumbing",
    name: "Rough Plumbing",
    order: 3,
    schedule_risk: "at_risk",
    description: "Relocate supply and drain lines for the new island sink.",
    start_date: "2026-09-13",
    end_date: "2026-09-20",
    is_started: true,
    progress: 40,
    status: MILESTONE_STATUSES.IN_PROGRESS,
    status_display: "In Progress",
    dependency_count: 1
  }),
  milestone({
    id: "demo-milestone-electrical",
    name: "Electrical",
    order: 4,
    schedule_risk: "on_track",
    description: "Add under-cabinet lighting circuits and island outlets.",
    start_date: "2026-09-21",
    end_date: "2026-09-25",
    status: MILESTONE_STATUSES.PLANNED,
    status_display: "Planned",
    dependency_count: 1
  }),
  milestone({
    id: "demo-milestone-cabinets",
    name: "Cabinets",
    order: 5,
    schedule_risk: "at_risk",
    description: "Order, deliver and install cabinetry.",
    start_date: "2026-09-26",
    end_date: "2026-10-17",
    status: MILESTONE_STATUSES.PLANNED,
    status_display: "Planned",
    planned_receivable: 12000,
    committed_receivable: 12000,
    planned_payable: 9000,
    committed_payable: 9000,
    financial_items: [
      financeCabinetsReceivable,
      financeCabinetsPayableSupplier,
      financeCabinetsPayableCrew
    ],
    dependency_count: 1
  }),
  milestone({
    id: "demo-milestone-countertops",
    name: "Countertops",
    order: 6,
    schedule_risk: "on_track",
    description: "Template, fabricate and install countertops.",
    start_date: "2026-10-20",
    end_date: "2026-10-27",
    status: MILESTONE_STATUSES.PLANNED,
    status_display: "Planned",
    dependency_count: 1
  }),
  milestone({
    id: "demo-milestone-final",
    name: "Final Installation",
    order: 7,
    schedule_risk: "high_risk",
    description: "Appliances, fixtures, punch list and final walkthrough.",
    start_date: "2026-10-28",
    end_date: "2026-11-05",
    status: MILESTONE_STATUSES.PENDING,
    status_display: "Pending",
    dependency_count: 1
  })
]

export const TOUR_DEMO_DEPENDENCIES: MilestoneDependency[] = [
  {
    id: "demo-dep-1",
    source_milestone: "demo-milestone-planning",
    target_milestone: "demo-milestone-demolition",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  },
  {
    id: "demo-dep-2",
    source_milestone: "demo-milestone-demolition",
    target_milestone: "demo-milestone-rough-plumbing",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  },
  {
    id: "demo-dep-3",
    source_milestone: "demo-milestone-rough-plumbing",
    target_milestone: "demo-milestone-electrical",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  },
  {
    id: "demo-dep-4",
    source_milestone: "demo-milestone-electrical",
    target_milestone: "demo-milestone-cabinets",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  },
  {
    id: "demo-dep-5",
    source_milestone: "demo-milestone-cabinets",
    target_milestone: "demo-milestone-countertops",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  },
  {
    id: "demo-dep-6",
    source_milestone: "demo-milestone-countertops",
    target_milestone: "demo-milestone-final",
    type: MilestoneDependencyType.FINISH_TO_START,
    type_display: "Finish to Start",
    lag_days: 0,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z"
  }
]

/** Every financial line across every milestone, flattened — exactly how the
 * real Finance tab's data is described in docs/kb/flows/project-milestones.md
 * ("financial line items live inside a milestone's `financial_items` array
 * and have no endpoint of their own"). Each line's `project_phase_name`
 * still reads "Cabinets"/"Demolition"/etc., so the Finance table's own
 * "Milestone" column shows the exact same connection a real project would. */
export const TOUR_DEMO_FINANCE_ITEMS: ProjectMilestoneFinanceItem[] =
  TOUR_DEMO_MILESTONES.flatMap((m) => m.financial_items)

export const TOUR_DEMO_FINANCE_SUMMARY = {
  receivable: TOUR_DEMO_FINANCE_ITEMS.filter(
    (item) => item.item_type === FINANCE_ITEM_TYPES.RECEIVABLE
  ).reduce((sum, item) => sum + Number(item.value), 0),
  payable: TOUR_DEMO_FINANCE_ITEMS.filter(
    (item) => item.item_type === FINANCE_ITEM_TYPES.PAYABLE
  ).reduce((sum, item) => sum + Number(item.value), 0),
  receivable_collected: 2500,
  payable_paid: 1800
}

/** The Cabinet Progress Invoice deliberately carries a `lines` entry
 * pointing at `CABINETS_RECEIVABLE_ID` — a real generated invoice's own
 * connection back to the Finance line it was issued against (see
 * `IssueInvoiceDrawer.tsx` in production), so the tour can show "this
 * invoice came from that Finance line, which came from that milestone." */
export const TOUR_DEMO_INVOICES: ProjectInvoice[] = [
  {
    id: "demo-invoice-deposit",
    project: TOUR_DEMO_PROJECT_ID,
    project_name: TOUR_DEMO_PROJECT.lead_name,
    invoice_number: "INV-1001",
    attachments: [],
    date_issued: "2026-09-01",
    due_date: "2026-09-01",
    amount: "9700.00",
    type: INVOICE_TYPES.RECEIVABLE,
    type_display: "Receivable",
    status: INVOICE_STATUSES.PAID,
    status_display: "Paid",
    assignee: null,
    assignee_name: null,
    customer: "demo-customer",
    customer_name: "Sarah Miller",
    project_type: projectTypeDetail.id,
    service_type: null,
    created_by: null,
    created_by_name: null,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z",
    origin: 2,
    origin_display: "External",
    paid_to_date: "9700.00",
    is_generated: false,
    is_locked: true
  },
  {
    id: "demo-invoice-cabinets",
    project: TOUR_DEMO_PROJECT_ID,
    project_name: TOUR_DEMO_PROJECT.lead_name,
    invoice_number: "INV-1002",
    attachments: [],
    date_issued: "2026-09-27",
    due_date: "2026-10-10",
    amount: "12000.00",
    type: INVOICE_TYPES.RECEIVABLE,
    type_display: "Receivable",
    status: INVOICE_STATUSES.PARTIALLY_PAID,
    status_display: "Partially Paid",
    assignee: null,
    assignee_name: null,
    customer: "demo-customer",
    customer_name: "Sarah Miller",
    project_type: projectTypeDetail.id,
    service_type: null,
    created_by: null,
    created_by_name: null,
    created: "2026-09-27T00:00:00Z",
    updated: "2026-09-27T00:00:00Z",
    origin: 1,
    origin_display: "Generated",
    paid_to_date: "5000.00",
    is_generated: true,
    is_locked: false,
    lines: [
      {
        id: "demo-invoice-line-cabinets",
        finance_line: CABINETS_RECEIVABLE_ID,
        amount: "12000.00"
      }
    ]
  },
  {
    id: "demo-invoice-final",
    project: TOUR_DEMO_PROJECT_ID,
    project_name: TOUR_DEMO_PROJECT.lead_name,
    invoice_number: "INV-1003",
    attachments: [],
    date_issued: "2026-11-01",
    due_date: "2026-11-10",
    amount: "26800.00",
    type: INVOICE_TYPES.RECEIVABLE,
    type_display: "Receivable",
    status: INVOICE_STATUSES.DRAFT,
    status_display: "Draft",
    assignee: null,
    assignee_name: null,
    customer: "demo-customer",
    customer_name: "Sarah Miller",
    project_type: projectTypeDetail.id,
    service_type: null,
    created_by: null,
    created_by_name: null,
    created: "2026-09-01T00:00:00Z",
    updated: "2026-09-01T00:00:00Z",
    origin: 1,
    origin_display: "Generated",
    paid_to_date: "0.00",
    is_generated: true,
    is_locked: false
  }
]

export const TOUR_DEMO_INVOICE_SNAPSHOT = {
  overdue: "0.00",
  paid: "9700.00",
  pending: "38800.00",
  total_outstanding: "31800.00"
}

export const TOUR_DEMO_FILES = [
  "Miller Contract.pdf",
  "Kitchen Plans.pdf",
  "Cabinet Selection.pdf",
  "Permit.pdf"
]

export const TOUR_DEMO_ACTIVITY = [
  "Contract signed",
  "Project created",
  "Milestone updated",
  "Invoice paid",
  "File uploaded"
]
